/**
 * Alertes Sessions Premium (Toast & Banner)
 * ==========================================
 * 
 * RÈGLES MOA PHASE 2:
 * - Non anxiogène: wording positif
 * - soft_20: Toast info léger (dismiss auto)
 * - soft_10: Banner sticky doré (dismiss manuel)
 * - Pas d'alerte pour B2B
 */

import React, { useState, useEffect } from 'react';
import { X, Sparkles, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Toast non-anxiogène pour soft_20 (10% restant)
 */
export function showSessionsLowToast(sessionsRemaining) {
  toast.info(
    <div className="flex items-center gap-3">
      <Sparkles className="w-5 h-5 text-purple-400 flex-shrink-0" />
      <div>
        <p className="font-medium text-white">
          Plus que {sessionsRemaining} sessions ce mois
        </p>
        <p className="text-sm text-slate-400">
          Pensez à vos prochaines séances !
        </p>
      </div>
    </div>,
    {
      duration: 5000,
      className: 'bg-slate-800 border border-purple-500/30'
    }
  );
}

/**
 * Toast de succès après recharge
 */
export function showRechargeSuccessToast(sessions) {
  toast.success(
    <div className="flex items-center gap-3">
      <Sparkles className="w-5 h-5 text-emerald-400 flex-shrink-0" />
      <div>
        <p className="font-medium text-white">
          🎉 {sessions} sessions ajoutées !
        </p>
        <p className="text-sm text-slate-400">
          Continuez votre progression
        </p>
      </div>
    </div>,
    {
      duration: 4000,
      className: 'bg-slate-800 border border-emerald-500/30'
    }
  );
}

/**
 * Banner sticky pour soft_10 (10% restant)
 */
export function SessionsLowBanner({ sessionsRemaining, onRechargeClick, onDismiss }) {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) return null;

  const handleDismiss = () => {
    setIsVisible(false);
    if (onDismiss) onDismiss();
  };

  return (
    <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-b border-amber-500/20 px-4 py-3">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-amber-400" />
          </div>
          <div>
            <p className="text-amber-200 font-medium text-sm">
              Plus que {sessionsRemaining} sessions ce mois
            </p>
            <p className="text-amber-400/70 text-xs">
              Rechargez pour ne pas être interrompu
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {onRechargeClick && (
            <button
              onClick={onRechargeClick}
              className="px-4 py-1.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-1"
            >
              Recharger
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
          
          <button
            onClick={handleDismiss}
            className="p-1.5 rounded-lg hover:bg-amber-500/10 transition-colors"
          >
            <X className="w-4 h-4 text-amber-400/70" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook pour gérer l'affichage des alertes
 */
export function useSessionAlerts() {
  const [shownAlerts, setShownAlerts] = useState(() => {
    // Récupérer les alertes déjà vues cette session
    const stored = sessionStorage.getItem('sessions_alerts_shown');
    return stored ? JSON.parse(stored) : {};
  });

  const markAlertShown = (alertType) => {
    const updated = { ...shownAlerts, [alertType]: true };
    setShownAlerts(updated);
    sessionStorage.setItem('sessions_alerts_shown', JSON.stringify(updated));
  };

  const shouldShowAlert = (alertType) => {
    return !shownAlerts[alertType];
  };

  const showAlertIfNeeded = (alertLevel, sessionsRemaining) => {
    if (alertLevel === 'soft_20' && shouldShowAlert('soft_20')) {
      showSessionsLowToast(sessionsRemaining);
      markAlertShown('soft_20');
    }
  };

  return {
    shouldShowAlert,
    showAlertIfNeeded,
    markAlertShown
  };
}

export default {
  showSessionsLowToast,
  showRechargeSuccessToast,
  SessionsLowBanner,
  useSessionAlerts
};
